import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Send, Phone, MapPin, Mail, Loader2, CheckCircle, Shield, ArrowRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import GlowingOrb from "../effects/GlowingOrb";
import { RevealOnScroll, MagneticButton } from "../effects/AnimatedElements";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultContent = {
  section_label: "Contacto",
  title: "Hablemos de tu Proyecto",
  subtitle: "Estamos listos para ayudarte a transformar tu negocio.",
  phone: "718-916-1401",
  address: "820 E 168TH ST BRONX NY 10459",
  website: "globalmetafora.com"
};

const ContactSection = ({ content }) => {
  const data = { ...defaultContent, ...content };
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setIsSuccess(true);
      toast.success(t("contact_success"));
      setFormData({ name: "", email: "", company: "", phone: "", message: "" });
      setTimeout(() => setIsSuccess(false), 3000);
    } catch (error) {
      toast.error(t("contact_error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: Phone, label: t("contact_phone_label"), value: data.phone, href: `tel:${data.phone}`, color: "#3b82f6" },
    { icon: MapPin, label: t("contact_address_label"), value: data.address, href: null, color: "#06b6d4" },
    { icon: Mail, label: t("contact_website_label"), value: data.website, href: `https://${data.website}`, color: "#8b5cf6" },
  ];

  return (
    <section
      id="contacto"
      ref={sectionRef}
      data-testid="contact-section"
      className="py-24 lg:py-32 relative overflow-hidden"
    >
      {/* Background effects */}
      <GlowingOrb color="#3b82f6" size={500} className="-bottom-40 left-1/4" />
      <div className="absolute inset-0 grid-pattern opacity-30" />

      <div className="section-container relative z-10">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left side - Info */}
          <RevealOnScroll direction="left">
            <motion.span
              className="text-[#3b82f6] text-sm font-semibold tracking-wider uppercase mb-4 block"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
            >
              {t("contact_label")}
            </motion.span>

            <motion.h2
              data-testid="contact-title"
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1 }}
            >
              {t("contact_title")}
            </motion.h2>

            <motion.p
              className="text-[#94a3b8] text-lg mb-10"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.2 }}
            >
              {t("contact_subtitle")}
            </motion.p>

            {/* Animated contact cards */}
            <div className="space-y-4 mb-10">
              {contactInfo.map((item, index) => (
                <motion.div
                  key={index}
                  className="glass-card p-4 flex items-center gap-4 group relative overflow-hidden"
                  initial={{ opacity: 0, x: -30 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.3 + index * 0.12 }}
                  whileHover={{
                    borderColor: `${item.color}80`,
                    boxShadow: `0 0 25px ${item.color}15`
                  }}
                >
                  <motion.div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{ background: `linear-gradient(90deg, ${item.color}08, transparent)` }}
                  />
                  <motion.div
                    className="w-12 h-12 flex items-center justify-center flex-shrink-0 relative z-10"
                    style={{ backgroundColor: `${item.color}20` }}
                    whileHover={{ scale: 1.1, rotate: 5 }}
                  >
                    <item.icon className="w-5 h-5" style={{ color: item.color }} />
                  </motion.div>
                  <div className="relative z-10">
                    <p className="text-white font-medium text-sm">{item.label}</p>
                    {item.href ? (
                      <a
                        href={item.href}
                        target={item.href.startsWith("https") ? "_blank" : undefined}
                        rel={item.href.startsWith("https") ? "noopener noreferrer" : undefined}
                        data-testid={`contact-${item.label.toLowerCase()}`}
                        className="text-[#94a3b8] hover:text-white transition-colors text-sm inline-flex items-center gap-1"
                      >
                        {item.value}
                        <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </a>
                    ) : (
                      <p data-testid="contact-address" className="text-[#94a3b8] text-sm">{item.value}</p>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Confidentiality badge */}
            <motion.div
              className="glass-card p-6 relative overflow-hidden"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.7 }}
              whileHover={{ borderColor: "rgba(59,130,246,0.4)" }}
            >
              <motion.div
                className="absolute top-0 left-0 w-full h-px"
                style={{ background: "linear-gradient(90deg, transparent, #3b82f6, transparent)" }}
                animate={{ backgroundPosition: ["0% 0%", "200% 0%"] }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
              />
              <div className="flex items-center gap-3 mb-2">
                <Shield className="w-5 h-5 text-[#3b82f6]" />
                <p className="text-[#3b82f6] font-semibold">{t("contact_confidential")}</p>
              </div>
              <p className="text-[#94a3b8] text-sm">{t("contact_confidential_desc")}</p>
            </motion.div>
          </RevealOnScroll>

          {/* Right side - Form */}
          <RevealOnScroll direction="right">
            <motion.form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="glass-card p-8 relative overflow-hidden"
              whileHover={{ borderColor: "rgba(59,130,246,0.3)" }}
            >
              {/* Animated top border */}
              <motion.div
                className="absolute top-0 left-0 h-px"
                style={{ background: "linear-gradient(90deg, #3b82f6, #06b6d4)" }}
                initial={{ width: "0%" }}
                animate={isInView ? { width: "100%" } : {}}
                transition={{ delay: 0.5, duration: 1 }}
              />

              <h3 className="text-xl font-bold text-white mb-2">{t("contact_form_title")}</h3>
              <p className="text-[#94a3b8] text-sm mb-6">{t("contact_form_subtitle")}</p>

              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                {[
                  { name: "name", label: t("contact_name"), type: "text", placeholder: t("contact_name_ph"), required: true },
                  { name: "email", label: t("contact_email"), type: "email", placeholder: t("contact_email_ph"), required: true },
                ].map((field) => (
                  <motion.div
                    key={field.name}
                    animate={{ scale: focusedField === field.name ? 1.02 : 1 }}
                    transition={{ duration: 0.2 }}
                  >
                    <label className="block text-[#94a3b8] text-sm mb-2">{field.label}</label>
                    <input
                      type={field.type}
                      name={field.name}
                      data-testid={`contact-input-${field.name}`}
                      value={formData[field.name]}
                      onChange={handleChange}
                      onFocus={() => setFocusedField(field.name)}
                      onBlur={() => setFocusedField(null)}
                      required={field.required}
                      className="contact-input w-full"
                      placeholder={field.placeholder}
                    />
                  </motion.div>
                ))}
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                {[
                  { name: "company", label: t("contact_company"), type: "text", placeholder: t("contact_company_ph") },
                  { name: "phone", label: t("contact_phone"), type: "tel", placeholder: t("contact_phone_ph") },
                ].map((field) => (
                  <motion.div
                    key={field.name}
                    animate={{ scale: focusedField === field.name ? 1.02 : 1 }}
                    transition={{ duration: 0.2 }}
                  >
                    <label className="block text-[#94a3b8] text-sm mb-2">{field.label}</label>
                    <input
                      type={field.type}
                      name={field.name}
                      data-testid={`contact-input-${field.name}`}
                      value={formData[field.name]}
                      onChange={handleChange}
                      onFocus={() => setFocusedField(field.name)}
                      onBlur={() => setFocusedField(null)}
                      className="contact-input w-full"
                      placeholder={field.placeholder}
                    />
                  </motion.div>
                ))}
              </div>

              <motion.div
                className="mb-6"
                animate={{ scale: focusedField === "message" ? 1.01 : 1 }}
                transition={{ duration: 0.2 }}
              >
                <label className="block text-[#94a3b8] text-sm mb-2">{t("contact_message")}</label>
                <textarea
                  name="message"
                  data-testid="contact-input-message"
                  value={formData.message}
                  onChange={handleChange}
                  onFocus={() => setFocusedField("message")}
                  onBlur={() => setFocusedField(null)}
                  required
                  rows={5}
                  className="contact-input w-full resize-none"
                  placeholder={t("contact_message_ph")}
                />
              </motion.div>

              <MagneticButton className="w-full">
                <motion.button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full flex items-center justify-center gap-2 relative overflow-hidden"
                  whileHover={{ boxShadow: "0 0 40px rgba(59,130,246,0.5)" }}
                  whileTap={{ scale: 0.98 }}
                >
                  {/* Shimmer effect */}
                  <motion.span
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    initial={{ x: "-100%" }}
                    whileHover={{ x: "100%" }}
                    transition={{ duration: 0.6 }}
                  />
                  <span className="relative flex items-center justify-center gap-2">
                    {isSubmitting ? (
                      <><Loader2 className="w-5 h-5 animate-spin" /><span>{t("contact_sending")}</span></>
                    ) : isSuccess ? (
                      <><CheckCircle className="w-5 h-5" /><span>{t("contact_sent")}</span></>
                    ) : (
                      <><span>{t("contact_send")}</span><Send className="w-5 h-5" /></>
                    )}
                  </span>
                </motion.button>
              </MagneticButton>
            </motion.form>
          </RevealOnScroll>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
